import { useEffect, useRef, useState } from 'react';

import {
  fetchOnboardingOptions,
  type OnboardingOptions,
} from 'features/onboarding/api/options';

/**
 * Module-level cache so that multiple components (Step 2, Step 5) on the same
 * page share a single backend round-trip. The data is small (≤ 100 rows total)
 * and stable for the lifetime of the SPA session.
 */
let cached: OnboardingOptions | null = null;
let inflight: Promise<OnboardingOptions> | null = null;

const load = (): Promise<OnboardingOptions> => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetchOnboardingOptions()
    .then((opts) => {
      cached = opts;
      return opts;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

interface UseOnboardingOptionsResult {
  data: OnboardingOptions | null;
  isLoading: boolean;
  error: Error | null;
}

export const useOnboardingOptions = (): UseOnboardingOptionsResult => {
  const [data, setData] = useState<OnboardingOptions | null>(cached);
  const [isLoading, setIsLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (cached) return;
    setIsLoading(true);
    load()
      .then((opts) => {
        if (mountedRef.current) setData(opts);
      })
      .catch((err: unknown) => {
        if (mountedRef.current)
          setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { data, isLoading, error };
};
